import React from 'react';
import { Select } from 'antd';
import { useLazyQuery, useQuery } from '@apollo/client';
import {
  useFilterLabelClassName,
  useFilterContainerClassName,
  useFilterInputContainerClassName
} from '../../utils/languageUtils';
import {
  GET_ALL_DSPS,
  SEARCH_DSPS_BY_NAME,
  GetAllDspsResponse,
  SearchDspsByNameResponse
} from '../../hooks/useDspOptions';
import { GET_DSPS_BY_WAREHOUSE_ID, GetDspsByWarehouseIdResponse, GetDspsByWarehouseIdVariables } from '../../graphql/queries/getDspsByWarehouseId';
import { GET_DSPS_BY_HUB_ID, GetDspsByHubIdResponse, GetDspsByHubIdVariables } from '../../graphql/queries/getDspsByHubId';
import { DspType } from '../../graphql/types/DspType';

interface DspSelectFilterProps {
  label: string;
  value?: string[];
  onChange: (value: string[] | undefined) => void;
  spanClassName?: string;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  warehouseId?: number; // 可选：根据仓库ID过滤DSP
  hubId?: number; // 可选：根据Hub ID过滤DSP
}

export const DspSelectFilter: React.FC<DspSelectFilterProps> = ({
  label,
  value,
  onChange,
  spanClassName,
  placeholder = 'All',
  className,
  warehouseId,
  hubId,
  disabled = false
}) => {
  // 根据是否有 warehouseId 或 hubId 选择查询方式
  const { data: allData, loading: allLoading } = useQuery<GetAllDspsResponse>(GET_ALL_DSPS, {
    skip: !!(warehouseId || hubId) // 如果有 warehouseId 或 hubId，跳过查询所有 DSP
  });
  const { data: warehouseDspData, loading: warehouseDspLoading } = useQuery<GetDspsByWarehouseIdResponse, GetDspsByWarehouseIdVariables>(
    GET_DSPS_BY_WAREHOUSE_ID,
    {
      variables: { warehouseId: warehouseId! },
      skip: !warehouseId || !!hubId // 如果没有 warehouseId 或有 hubId，跳过仓库 DSP 查询
    }
  );
  const { data: hubDspData, loading: hubDspLoading } = useQuery<GetDspsByHubIdResponse, GetDspsByHubIdVariables>(
    GET_DSPS_BY_HUB_ID,
    {
      variables: { hubId: hubId! },
      skip: !hubId || !!warehouseId // 如果没有 hubId 或有 warehouseId，跳过 Hub DSP 查询
    }
  );

  const [search, { data: searchData, loading: searchLoading }] = useLazyQuery<SearchDspsByNameResponse>(SEARCH_DSPS_BY_NAME);

  const [searchText, setSearchText] = React.useState('');
  const debouncedKeyword = useDebounce(searchText, 300);

  React.useEffect(() => {
    if (debouncedKeyword && debouncedKeyword.trim().length > 0) {
      search({ variables: { keyword: debouncedKeyword.trim() } });
    }
  }, [debouncedKeyword, search]);

  // 当 warehouseId 或 hubId 变化时，检查并清空不在新 DSP 列表中的已选值
  React.useEffect(() => {
    const dspList = warehouseId ? warehouseDspData?.dspList : hubId ? hubDspData?.dspList : null;
    if ((warehouseId || hubId) && dspList && value && value.length > 0) {
      const validDspIds = new Set(dspList.map((dsp: DspType) => String(dsp.id)));
      const invalidValues = value.filter(v => !validDspIds.has(v));
      if (invalidValues.length > 0) {
        // 如果有一些无效值，清空所有选择（因为 warehouse/hub 切换了）
        onChange(undefined);
      }
    }
  }, [warehouseId, hubId, warehouseDspData, hubDspData, value, onChange]);

  const options = React.useMemo(() => {
    // 如果有搜索关键词，优先使用搜索结果
    if (debouncedKeyword && searchData?.searchDspsByName?.length) {
      const searchResults = searchData.searchDspsByName;
      // 如果指定了 warehouseId 或 hubId，需要过滤搜索结果
      const dspList = warehouseId ? warehouseDspData?.dspList : hubId ? hubDspData?.dspList : null;
      if ((warehouseId || hubId) && dspList) {
        const dspIds = new Set(dspList.map((dsp: DspType) => dsp.id));
        return searchResults
          .filter(dsp => dspIds.has(dsp.id))
          .map(dsp => ({
            label: dsp.name,
            value: dsp.id,
            searchText: dsp.name
          }));
      }
      return searchResults.map(dsp => ({
        label: dsp.name,
        value: dsp.id,
        searchText: dsp.name
      }));
    }

    // 如果有 warehouseId，使用仓库关联的 DSP
    if (warehouseId && warehouseDspData?.dspList) {
      return warehouseDspData.dspList.map(dsp => ({
        label: dsp.name,
        value: dsp.id,
        searchText: dsp.name
      }));
    }

    // 如果有 hubId，使用 Hub 关联的 DSP
    if (hubId && hubDspData?.dspList) {
      return hubDspData.dspList.map((dsp: DspType) => ({
        label: dsp.name,
        value: dsp.id,
        searchText: dsp.name
      }));
    }

    // 否则使用所有 DSP
    return (allData?.allDsps || []).map(dsp => ({
      label: dsp.name,
      value: dsp.id,
      searchText: dsp.name
    }));
  }, [allData, warehouseDspData, hubDspData, searchData, debouncedKeyword, warehouseId, hubId]);

  const loading = allLoading || warehouseDspLoading || hubDspLoading || searchLoading;

  // 使用统一的样式 Hook，根据语言自动返回合适的样式类名
  const containerClassName = useFilterContainerClassName(className);
  const labelClassName = useFilterLabelClassName(spanClassName);
  const inputContainerClassName = useFilterInputContainerClassName();

  return (
    <div className={containerClassName}>
      <span className={labelClassName}>{label}</span>
      <div className={inputContainerClassName}>
        <Select
          showSearch
          allowClear
          mode="multiple"
          placeholder={placeholder}
          value={value}
          onChange={(val) => onChange((val as string[] | undefined))}
          loading={loading}
          options={options}
          filterOption={false}
          onSearch={(val) => setSearchText(val)}
          className="w-full"
          disabled={disabled}
          size="middle"
        />
      </div>
    </div>
  );
};

function useDebounce<T>(value: T, delay = 300): T {
  const [debounced, setDebounced] = React.useState(value);
  React.useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

export default DspSelectFilter;
